import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, AlertCircle, CheckCircle, FileText, Trash2 } from 'lucide-react';
import SavedComparisonView from './SavedComparisonView';
import FileUpload from './FileUpload';
import BulkUploadModal from './BulkUploadModal';

const ComplianceReport = ({ user }) => {
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  // Upload functionality state
  const [chargebeeInstances, setChargebeeInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  // Bulk upload state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkComplete, setBulkComplete] = useState(false);

  useEffect(() => {
    fetchComplianceData();
    fetchChargebeeInstances();
  }, []);

  const fetchComplianceData = async () => {
    try {
      // Fetch saved comparisons instead of placeholder data
      const userHash = user?.user_hash || localStorage.getItem('userHash');
      const savedComparisonsRes = await axiosInstance.get('/api/saved-comparisons', {
        headers: {
          Authorization: userHash
        }
      });
      const comparisons = savedComparisonsRes.data.comparisons || [];
      
      // Generate report data from saved comparisons
      const compliantCount = comparisons.filter(c => c.status === 'Compliant').length;
      const needsReviewCount = comparisons.filter(c => c.status === 'Needs Review').length;
      const invalidSOFCount = comparisons.filter(c => c.status === 'Subscription Not Found').length;
      const totalValidations = comparisons.length;
      
      const generatedReport = {
        total_validations: totalValidations,
        compliant: compliantCount,
        non_compliant: invalidSOFCount, // Use Subscription Not Found count for non-compliant
        needs_review: needsReviewCount,
        sox_risk_score: totalValidations > 0 ? Math.round(((needsReviewCount + invalidSOFCount) / totalValidations) * 100) : 0,
       
      };
      
      setReport(generatedReport);
      setHistory(comparisons);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
      // Fallback to empty data if API fails
      setReport({
        total_validations: 0,
        compliant: 0,
        non_compliant: 0,
        needs_review: 0,
        sox_risk_score: 0,
        recommendations: []
      });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComparison = async (comparisonId, event) => {
    // Prevent row click when clicking delete button
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this comparison? This action cannot be undone.')) {
      return;
    }

    setDeleting(comparisonId);
    try {
      const userHash = user?.user_hash || localStorage.getItem('userHash');
      await axiosInstance.delete(`/api/saved-comparisons/${comparisonId}`, {
        headers: {
          Authorization: userHash
        }
      });
      
      // Refresh the data after successful deletion
      await fetchComplianceData();
      
      // Close the comparison view if the deleted item was currently selected
      if (selectedComparison && selectedComparison.id === comparisonId) {
        setSelectedComparison(null);
      }
    } catch (error) {
      console.error('Error deleting comparison:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete comparison: ' + (error.response?.data?.error || error.message)));
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAll = async () => {
    if (history.length === 0) {
      alert('No comparisons to delete.');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete ALL ${history.length} validation entries? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingAll(true);
    try {
      const userHash = user?.user_hash || localStorage.getItem('userHash');
      await axiosInstance.delete('/api/saved-comparisons', {
        headers: {
          Authorization: userHash
        }
      });
      
      // Refresh the data after successful deletion
      await fetchComplianceData();
      
      // Close the comparison view if any item was currently selected
      if (selectedComparison) {
        setSelectedComparison(null);
      }
      
      alert('All validation entries have been successfully deleted.');
    } catch (error) {
      console.error('Error deleting all comparisons:', error);
      alert(resolve403ErrorMessage(error, 'Failed to delete all comparisons: ' + (error.response?.data?.error || error.message)));
    } finally {
      setDeletingAll(false);
    }
  };

  const fetchChargebeeInstances = async () => {
    try {
      const response = await axiosInstance.get('/api/chargebee-instances');
      setChargebeeInstances(response.data);
      if (response.data.length > 0) {
        const testInstance = response.data.find(instance => instance.id === 'test');
        if (testInstance) {
          setSelectedInstance('test');
        } else {
          setSelectedInstance(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching Chargebee instances:', error);
    }
  };

  const handleFileUpload = async (files) => {
    // Handle both single file and multiple files
    const fileArray = Array.isArray(files) ? files : [files];
    
    if (fileArray.length === 1) {
      // Single file - process normally
      await processSingleFile(fileArray[0]);
    } else {
      // Multiple files - show modal and process sequentially
      await processBulkFiles(fileArray);
    }
  };

  const processSingleFile = async (file) => {
    setUploadLoading(true);
    try {
      const result = await processFile(file);
      await fetchComplianceData();
      alert(`SOF validation completed! Status: ${result.status}`);
    } catch (error) {
      console.error('Error processing SOF:', error);
      if (error.response?.data) {
        console.error('Error response data:', error.response.data);
      }
      alert(resolve403ErrorMessage(
        error,
        'Error processing SOF: ' + (error.response?.data?.error || error.message)
      ));
    } finally {
      setUploadLoading(false);
    }
  };

  const processBulkFiles = async (files) => {
    // Initialize bulk upload state
    const fileTrackingArray = files.map(file => ({
      name: file.name,
      status: 'pending',
      result: null
    }));
    
    setBulkFiles(fileTrackingArray);
    setShowBulkModal(true);
    setBulkComplete(false);
    setUploadLoading(true);

    // Process files one by one
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Update status to processing
      setBulkFiles(prev => prev.map((f, index) => 
        index === i ? { ...f, status: 'processing' } : f
      ));

      try {
        const result = await processFile(file);
        
        // Update status to completed
        setBulkFiles(prev => prev.map((f, index) => 
          index === i ? { ...f, status: 'completed', result } : f
        ));
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        
        // Update status to error
        setBulkFiles(prev => prev.map((f, index) => 
          index === i ? { 
            ...f, 
            status: 'error', 
            result: resolve403ErrorMessage(error, error.response?.data?.error || error.message)
          } : f
        ));
      }
    }

    // Mark as complete and refresh data
    setBulkComplete(true);
    setUploadLoading(false);
    await fetchComplianceData();
  };

  const processFile = async (file) => {
    const uploadTimestamp = new Date().toISOString();
    const formData = new FormData();
    formData.append('file', file);
    
    // Convert file to base64 for storage
    const fileToBase64 = () => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data:application/pdf;base64, prefix
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = error => reject(error);
      });
    };
    
    let fileBase64;
    try {
      fileBase64 = await fileToBase64();
    } catch (error) {
      console.error('Error converting file to base64:', error);
      fileBase64 = ''; // Continue without file storage
    }

    let parseResponse, compareResponse, signatureResponse;
    
    try {
      console.log('Starting SOF processing...');
      console.log('Selected instance:', selectedInstance);
      console.log('File name:', file.name);
      
      // Step 1: Parse SOF fields
      console.log('Step 1: Parsing SOF fields...');
      parseResponse = await axiosInstance.post('/api/parse-sof-fields', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('SOF fields extracted successfully:', parseResponse.data);
      
      // Step 2: Detect signature
      console.log('Step 2: Detecting signature...');
      signatureResponse = await axiosInstance.post('/api/detect-signature', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Signature detection response:', signatureResponse.data);
      
      // Step 3: Compare to Chargebee
      console.log('Step 3: Comparing to Chargebee...');
      console.log('Sending data to comparison:', parseResponse.data);
      compareResponse = await axiosInstance.post('/api/compare-sof-to-chargebee', {
        sof_data: parseResponse.data,
        chargebee_instance: selectedInstance || 'test'
      });
      
      console.log('Compare response received:', compareResponse.data);
    } catch (parseError) {
      console.error('Error parsing SOF or comparing to Chargebee:', parseError);
      console.error('Error details:', {
        message: parseError.message,
        response: parseError.response?.data,
        status: parseError.response?.status,
        config: parseError.config?.url
      });

      if (parseError?.response?.status === 403) {
        throw parseError;
      }
      
      // If parsing fails, treat as invalid template and save with "Subscription Not Found" status
      const sofId = `SOF_${Date.now()}_${file.name}`;
      
      // Try to get signature info even if parsing failed
      let signature = false;
      let signatureDate = null;
      try {
        if (signatureResponse && signatureResponse.data) {
          signature = signatureResponse.data.signature;
          signatureDate = signatureResponse.data.signature_date;
        }
      } catch (sigError) {
        console.log('Signature detection also failed, using defaults');
      }
      
      const payload = {
        sof_id: sofId,
        user_hash: user?.user_hash || localStorage.getItem('userHash'),
        upload_date: uploadTimestamp,
        sof_data: {
          billing_contact_information: {},
          product_information: {},
          subscription_details: {}
        },
        subscription_data: {},
        comparison_results: {
          match_count: 0,
          message: 'Failed to parse SOF file - invalid template or format',
          matching_subscriptions: []
        },
        errors: -1,
        status: 'Subscription Not Found',
        signature: signature,
        signature_date: signatureDate,
        file_data: fileBase64,
        file_name: file.name
      };

      const userHash = user?.user_hash || localStorage.getItem('userHash');
      await axiosInstance.post('/api/save-comparison', payload, {
        headers: {
          Authorization: userHash
        }
      });
      return { status: 'Subscription Not Found', errors: -1, sof_id: sofId };
    }
    
    // Step 3: Prepare and save comparison
    let errors, status;
    if (compareResponse.data.match_count === 0) {
      errors = -1;
      status = 'Subscription Not Found';
    } else {
      let errorCount = 0;
      const subscription = compareResponse.data.matching_subscriptions[0].subscription || compareResponse.data.matching_subscriptions[0];
      const customer = compareResponse.data.matching_subscriptions[0].customer || {};
      
      // Check Customer match
      const sofCustomer = (compareResponse.data.sof_data.billing_contact_information?.customer || '').toLowerCase();
      const chargebeeCustomer = (customer.company || '').toLowerCase();
      if (sofCustomer !== chargebeeCustomer) errorCount++;

      // Check Email match
      const sofEmail = (compareResponse.data.sof_data.billing_contact_information?.email_address || '').toLowerCase();
      const chargebeeEmail = (customer.email || '').toLowerCase();
      if (sofEmail !== chargebeeEmail) errorCount++;

      // Check Total Amount match - use proper MRR calculation
      const sofAmount = parseFloat((compareResponse.data.sof_data.product_information?.total_net_price || '0').replace(/[^0-9.-]+/g, ''));
      
      // Calculate Chargebee amount using the same logic as frontend display
      let chargebeeAmount;
      if (subscription.mrr !== undefined && subscription.mrr > 0) {
        chargebeeAmount = subscription.mrr / 100; // Convert cents to dollars
      } else if (subscription.calculated_total_amount_dollars !== undefined) {
        chargebeeAmount = subscription.calculated_total_amount_dollars;
      } else if (subscription.subscription_items && Array.isArray(subscription.subscription_items)) {
        const totalCents = subscription.subscription_items.reduce((total, item) => total + (item.amount || 0), 0);
        chargebeeAmount = totalCents / 100;
      } else {
        const amount = subscription.plan_amount || subscription.amount || 0;
        chargebeeAmount = amount / 100;
      }
      
      // Allow for small rounding differences (within $0.01)
      const amountDifference = Math.abs(sofAmount - chargebeeAmount);
      const amountMatch = amountDifference < 0.01;
      if (!amountMatch) errorCount++;

      // Check Product Instance Domain match - use contains logic
      const sofDomain = compareResponse.data.sof_data.product_information?.product_instance_domain || '';
      const chargebeeDomain = subscription.cf_values?.product_instance_domain || 
                             subscription.cf_product_instance_domain || 
                             subscription.custom_fields?.find(cf => cf.name === 'cf_product_instance_domain')?.value || 
                             '';
      
      // Also check alternative locations for domain
      let alternativeChargebeeDomain = '';
      for (const key in subscription) {
        if (key.includes('product_instance_domain') || key.includes('domain')) {
          if (subscription[key] && !alternativeChargebeeDomain) {
            alternativeChargebeeDomain = subscription[key];
          }
        }
      }
      
      const finalChargebeeDomain = chargebeeDomain || alternativeChargebeeDomain || subscription.id || '';
      
      // Consider it a match if:
      // 1. SOF domain contains the Chargebee domain (like 16BRx0asdfUpekth6EMj.freshdesk.com contains 16BRx0Upekth6EMj)
      // 2. SOF domain equals the Chargebee domain
      // 3. Both are empty (no error if both missing)
      const domainMatch = (!finalChargebeeDomain && !sofDomain) || // Both empty
                         (finalChargebeeDomain && sofDomain && 
                          (sofDomain === finalChargebeeDomain || sofDomain.includes(finalChargebeeDomain)));
      
      console.log('Domain Comparison Debug:');
      console.log(`  SOF Domain: "${sofDomain}"`);
      console.log(`  Chargebee Domain: "${finalChargebeeDomain}"`);
      console.log(`  Match: ${domainMatch}`);
      
      if (!domainMatch) errorCount++;
      
      errors = errorCount;
      status = errors === 0 ? 'Compliant' : 'Needs Review';
      
      console.log(`Final Error Count: ${errors}`);
      console.log(`Status: ${status}`);
    }
    
    // Generate SOF ID
    const sofId = compareResponse.data.sof_data.product_information?.product_instance_id || 
                 compareResponse.data.sof_data.product_information?.product_instance_domain || 
                 `SOF_${Date.now()}`;

    // Get signature information
    const signature = signatureResponse?.data?.signature || false;
    const signatureDate = signatureResponse?.data?.signature_date || null;

    const payload = {
      sof_id: sofId,
      user_hash: user?.user_hash || localStorage.getItem('userHash'),
      upload_date: uploadTimestamp,
      sof_data: compareResponse.data.sof_data,
      subscription_data: (compareResponse.data.matching_subscriptions && compareResponse.data.matching_subscriptions.length > 0) 
        ? compareResponse.data.matching_subscriptions[0] 
        : {},
      comparison_results: {
        match_count: compareResponse.data.match_count,
        message: compareResponse.data.message,
        matching_subscriptions: compareResponse.data.matching_subscriptions || []
      },
      errors: errors,
      status: status,
      signature: signature,
      signature_date: signatureDate,
      file_data: fileBase64,
      file_name: file.name
    };

    const userHash = user?.user_hash || localStorage.getItem('userHash');
    await axiosInstance.post('/api/save-comparison', payload, {
      headers: {
        Authorization: userHash
      }
    });
    
    return { status, errors, sof_id: sofId };
  };


  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const complianceData = [
    { name: 'Compliant', value: report.compliant, color: '#10B981' },
    { name: 'Subscription Not Found', value: report.non_compliant, color: '#EF4444' },
    { name: 'Needs Review', value: report.needs_review, color: '#F59E0B' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          SOX Compliance Dashboard
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-800 font-medium">Total Validations</p>
                <p className="text-2xl font-bold text-blue-900">{report.total_validations}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Compliant</p>
                <p className="text-2xl font-bold text-green-900">{report.compliant}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Subscription Not Found</p>
                <p className="text-2xl font-bold text-red-900">{report.non_compliant}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">SOX Risk Score</p>
                <p className="text-2xl font-bold text-purple-900">{report.sox_risk_score}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4">
              Compliance Distribution
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={complianceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {complianceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4">
              Quick SOF Upload
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Chargebee Instance
                </label>
                <select
                  value={selectedInstance}
                  onChange={(e) => setSelectedInstance(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {chargebeeInstances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.region})
                    </option>
                  ))}
                </select>
              </div>
              <FileUpload onFileUpload={handleFileUpload} loading={uploadLoading} allowMultiple={true} />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-md font-semibold text-gray-900">
            Recent Validation History
          </h3>
          <button
            onClick={handleDeleteAll}
            disabled={deletingAll || history.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={history.length === 0 ? "No entries to delete" : "Delete all validation entries"}
          >
            {deletingAll ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Deleting All...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete All
              </>
            )}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SOF ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Errors
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {history.map((item) => (
                <tr 
                  key={item.id} 
                  onClick={() => setSelectedComparison(item)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-800 hover:text-blue-800">
                    {item.sof_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.upload_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.status === 'Compliant'
                        ? 'bg-green-100 text-green-800'
                        : item.status === 'Subscription Not Found'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.errors === -1 ? 'INVALID' : item.errors}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={(e) => handleDeleteComparison(item.id, e)}
                      disabled={deleting === item.id}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-red-50 transition-colors"
                      title="Delete comparison"
                    >
                      {deleting === item.id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No saved comparisons yet. Complete a SOF comparison to see history here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedComparison && (
        <SavedComparisonView 
          comparison={selectedComparison}
          onClose={() => setSelectedComparison(null)}
        />
      )}

      <BulkUploadModal
        isOpen={showBulkModal}
        files={bulkFiles}
        isComplete={bulkComplete}
        onClose={() => {
          setShowBulkModal(false);
          setBulkFiles([]);
          setBulkComplete(false);
        }}
      />
    </div>
  );
};

export default ComplianceReport;